import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: '#050A12' }}>

      {/* Parallax background */}
      <div style={{ position: 'absolute', inset: 0, transform: `translateY(${scrollY * 0.35}px)`, willChange: 'transform' }}>
        <img
          src="https://images.unsplash.com/photo-1611615656662-272544791c31"
          alt="Winding mountain road through Norwegian fjords with dramatic sky and snow-capped peaks"
          style={{ width: '100%', height: '120%', objectFit: 'cover', objectPosition: 'center 30%', filter: 'brightness(0.38) contrast(1.2) saturate(0.7)' }}
        />
      </div>

      {/* Grain texture overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
        opacity: 0.6, pointerEvents: 'none', zIndex: 2
      }} />

      {/* Hard diagonal color block — bottom left */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: '40%', height: '35%',
        background: '#3B6FD4',
        clipPath: 'polygon(0 40%, 100% 100%, 0 100%)',
        opacity: 0.12, zIndex: 1
      }} />

      {/* Vertical sidebar — hidden on mobile */}
      {!isMobile && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: '52px',
          background: 'rgba(59,111,212,0.06)',
          borderRight: '1px solid rgba(59,111,212,0.15)',
          zIndex: 10,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: '0'
        }}>
          <div style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.6rem', letterSpacing: '0.35em',
            color: 'rgba(59,111,212,0.7)', textTransform: 'uppercase',
            whiteSpace: 'nowrap'
          }}>NordPass — Norges veier — 2026</div>
          <div style={{ width: '1px', height: '60px', background: 'rgba(59,111,212,0.3)', margin: '16px 0' }} />
          <div style={{ fontSize: '0.5rem', color: 'rgba(59,111,212,0.4)', fontFamily: 'monospace', letterSpacing: '0.1em' }}>N</div>
        </div>
      )}

      {/* Main content */}
      <div style={{
        position: 'relative', zIndex: 10,
        paddingLeft: isMobile ? '20px' : '80px',
        paddingRight: isMobile ? '20px' : '5%',
        paddingTop: isMobile ? '100px' : '130px',
        paddingBottom: isMobile ? '60px' : '80px',
        minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center'
      }}>

        {/* Tiny stamp — hidden on mobile to avoid overlap */}
        {!isMobile && (
          <div style={{
            position: 'absolute', top: '140px', right: '6%',
            border: '2px solid rgba(232,148,26,0.5)',
            padding: '6px 12px',
            transform: 'rotate(3deg)',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.65rem', color: 'rgba(232,148,26,0.7)',
            letterSpacing: '0.05em', whiteSpace: 'nowrap'
          }}>siden 2019 ✦</div>
        )}

        {/* Eyebrow — tiny, uppercase, tracked */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '28px', height: '1px', background: '#E8941A' }} />
          <span style={{
            fontFamily: 'monospace', fontSize: '0.6rem',
            letterSpacing: '0.3em', color: '#E8941A',
            textTransform: 'uppercase', fontWeight: 400
          }}>Norges ledende veihjelp</span>
        </div>

        {/* GIANT headline — brutalist stacked */}
        <div style={{ position: 'relative', marginBottom: '0' }}>

          {/* Line 1 — massive */}
          <div style={{
            fontFamily: '"Arial Black", "Impact", sans-serif',
            fontSize: isMobile ? 'clamp(3.5rem, 18vw, 5.5rem)' : 'clamp(5rem, 13vw, 11rem)',
            fontWeight: 900,
            color: '#EEF0F2',
            lineHeight: 0.88,
            letterSpacing: '-0.05em',
            textTransform: 'uppercase',
            margin: 0
          }}>NAVIGER</div>

          {/* Line 2 — outline only, offset right */}
          <div style={{
            fontFamily: '"Arial Black", "Impact", sans-serif',
            fontSize: isMobile ? 'clamp(3.5rem, 18vw, 5.5rem)' : 'clamp(5rem, 13vw, 11rem)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px #3B6FD4',
            lineHeight: 0.88,
            letterSpacing: '-0.05em',
            textTransform: 'uppercase',
            marginLeft: isMobile ? '16px' : 'clamp(24px, 5vw, 100px)',
            margin: 0
          }}>NORGES</div>

          {/* Line 3 — italic serif, different weight entirely */}
          <div style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: isMobile ? 'clamp(1.8rem, 9vw, 3rem)' : 'clamp(3rem, 7vw, 6.5rem)',
            fontWeight: 400,
            color: '#E8941A',
            lineHeight: 0.95,
            letterSpacing: '-0.01em',
            paddingLeft: isMobile ? '8px' : 'clamp(12px, 2vw, 40px)'
          }}>veier med trygghet.</div>

          {/* Annotation — hidden on mobile */}
          {!isMobile && (
            <div style={{
              position: 'absolute',
              right: '8%', top: '20%',
              transform: 'rotate(-8deg)',
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '0.85rem', color: 'rgba(238,240,242,0.35)',
              lineHeight: 1.4, maxWidth: '140px', textAlign: 'center',
              borderBottom: '1px solid rgba(238,240,242,0.15)',
              paddingBottom: '6px'
            }}>fra Nordkapp<br />til Lindesnes</div>
          )}
        </div>

        {/* Pull quote — editorial style */}
        <div style={{
          marginTop: '36px',
          display: 'grid',
          gridTemplateColumns: '3px 1fr',
          gap: '20px',
          maxWidth: isMobile ? '100%' : '520px'
        }}>
          <div style={{ background: '#3B6FD4', alignSelf: 'stretch' }} />
          <p style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: 'clamp(0.9rem, 1.4vw, 1.2rem)',
            color: 'rgba(238,240,242,0.65)',
            lineHeight: 1.75, margin: 0
          }}>
            Sanntids veikart, bomkalkulator og full kjøretøyoversikt —
            <em style={{ color: 'rgba(238,240,242,0.9)', fontStyle: 'normal', fontWeight: 700 }}> abonnementet som gjør norske veier enkle.</em>
          </p>
        </div>

        {/* CTA row */}
        <div style={{ marginTop: '40px', display: 'flex', alignItems: 'center', gap: isMobile ? '20px' : '32px', flexWrap: 'wrap' }}>
          <Link
            to="/login"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              padding: isMobile ? '14px 24px' : '18px 36px',
              background: '#3B6FD4', color: '#EEF0F2',
              fontFamily: '"Arial Black", sans-serif',
              fontSize: '0.75rem', fontWeight: 900,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              textDecoration: 'none',
              clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
            }}
            className="btn-spring btn-aurora"
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2A5BC4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#3B6FD4'; }}>
            Kom i gang gratis
            <Icon name="ArrowRight" size={14} color="#EEF0F2" />
          </Link>

          <Link
            to="/nordic-map-dashboard"
            style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '1rem', color: 'rgba(238,240,242,0.55)',
              textDecoration: 'underline', textUnderlineOffset: '4px',
              textDecorationColor: 'rgba(238,240,242,0.2)'
            }}
            className="nav-spring"
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(238,240,242,0.9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(238,240,242,0.55)'; }}>
            Se veikart →
          </Link>
        </div>

        {/* Bottom floating stat — hidden on mobile */}
        {!isMobile && (
          <div style={{
            position: 'absolute', bottom: '60px', right: '6%',
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px'
          }}>
            <div style={{
              fontFamily: '"Arial Black", sans-serif',
              fontSize: '3.5rem', fontWeight: 900,
              color: 'rgba(238,240,242,0.08)',
              letterSpacing: '-0.06em', lineHeight: 1
            }}>47 000</div>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.55rem',
              letterSpacing: '0.2em', color: 'rgba(138,149,163,0.4)',
              textTransform: 'uppercase'
            }}>aktive brukere</div>
          </div>
        )}

        {/* Scroll indicator — hidden on mobile */}
        {!isMobile && (
          <div style={{
            position: 'absolute', bottom: '32px', left: '80px',
            display: 'flex', alignItems: 'center', gap: '10px'
          }}>
            <div style={{
              width: '1px', height: '40px',
              background: 'linear-gradient(to bottom, transparent, rgba(59,111,212,0.6))'
            }} />
            <span style={{
              fontFamily: 'monospace', fontSize: '0.55rem',
              letterSpacing: '0.2em', color: 'rgba(138,149,163,0.4)',
              textTransform: 'uppercase', writingMode: 'vertical-rl'
            }}>scroll</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;